/**
 * ⌘⇧K (Mac) / Ctrl+Shift+K: put the cursor in the input that matters where the user
 * is looking — an agent chat's composer, a card's comment box. "Where they are
 * looking" is the topmost open dialog or sheet (the task sheet, the palette), else
 * the focused window (`[data-window]`), else the page. Inside that, a chat composer
 * (`[data-composer]`, every `ComposerTextarea`) wins, the last one shown, since a
 * composer sits under its thread; failing one, the first text field shown.
 */

export const isFocusInputShortcut = (event: Pick<KeyboardEvent, "key" | "code" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">): boolean =>
  (event.metaKey || event.ctrlKey) && event.shiftKey && !event.altKey && (event.code === "KeyK" || event.key.toLowerCase() === "k");

const FIELDS =
  'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="url"], [contenteditable="true"]';

const usable = (el: HTMLElement) =>
  !el.matches(':disabled, [readonly], [aria-hidden="true"] *') && !el.closest("[inert]") && el.getClientRects().length > 0;

/** The element whose input the shortcut should reach. */
export function inputScope(doc: Document, focusedWindowId: string | null): ParentNode {
  const dialogs = [...doc.querySelectorAll<HTMLElement>('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')];
  const top = dialogs.at(-1);
  if (top) return top;
  const win = focusedWindowId ? doc.querySelector<HTMLElement>(`[data-window="${CSS.escape(focusedWindowId)}"]`) : null;
  return win ?? doc;
}

/** The input to focus in `scope`, or null when it has none. */
export function primaryInput(scope: ParentNode): HTMLElement | null {
  const composers = [...scope.querySelectorAll<HTMLElement>("[data-composer]")].filter(usable);
  if (composers.length) return composers.at(-1)!;
  return [...scope.querySelectorAll<HTMLElement>(FIELDS)].find(usable) ?? null;
}

/** Focuses it with the caret at the end; false when there was nothing to focus. */
export function focusPrimaryInput(doc: Document, focusedWindowId: string | null): boolean {
  const input = primaryInput(inputScope(doc, focusedWindowId));
  if (!input) return false;
  input.focus();
  if (input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement) {
    const end = input.value.length;
    try {
      input.setSelectionRange(end, end);
    } catch {
      /* an input type without a selection */
    }
  }
  return true;
}
